import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

const TABS = [
  { id: 'ai_image', label: 'From My Images', emoji: '\u{1F5BC}\uFE0F' },
  { id: 'upload', label: 'Upload a Photo', emoji: '\u{1F4F7}' },
  { id: 'describe', label: 'Describe', emoji: '\u{270D}\uFE0F' },
];

const CharacterCreator = ({ recentAiImages = [], coinBalance = 0, csrfToken }) => {
  const [activeTab, setActiveTab] = useState('ai_image');
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createdCharacter, setCreatedCharacter] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  const canAfford = coinBalance >= 50;

  const handleUploadChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setUploadPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const pollStatus = (characterId, redirectUrl) => {
    setStatusMessage('Analyzing your character...');
    let attempts = 0;

    const check = () => {
      attempts++;
      if (attempts > 40) {
        setStatusMessage('Taking longer than expected. Redirecting...');
        window.location.href = redirectUrl;
        return;
      }

      fetch(`/characters/${characterId}/check_status`)
        .then(r => r.json())
        .then(data => {
          if (data.status === 'ready') {
            setStatusMessage('Done! Redirecting...');
            window.location.href = redirectUrl;
          } else if (data.status === 'failed') {
            setError('Character creation failed. Your coins have been refunded.');
            setLoading(false);
            setCreatedCharacter(null);
          } else {
            setStatusMessage(
              data.status === 'generating_references'
                ? 'Creating reference images...'
                : 'Analyzing your character...'
            );
            setTimeout(check, 3000);
          }
        })
        .catch(() => setTimeout(check, 3000));
    };

    setTimeout(check, 3000);
  };

  const handleCreateFromImage = async () => {
    if (!selectedImage) return;
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/characters/from_chat_image', {
        chat_image_id: selectedImage.id,
        name: name || '',
        authenticity_token: csrfToken,
      });

      if (response.data.success) {
        setCreatedCharacter(response.data.character);
        pollStatus(response.data.character.id, response.data.redirect_url);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create character.');
      setLoading(false);
    }
  };

  const handleCreateFromUpload = async () => {
    if (!uploadFile) return;
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', uploadFile);
      formData.append('name', name || '');
      formData.append('authenticity_token', csrfToken);

      const response = await axios.post('/characters/from_upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        setCreatedCharacter(response.data.character);
        pollStatus(response.data.character.id, response.data.redirect_url);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create character.');
      setLoading(false);
    }
  };

  const handleCreateFromDescription = async () => {
    if (!description.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/characters', {
        name: name || '',
        description,
        authenticity_token: csrfToken,
      });

      // Form POST redirect is handled server-side
      // If we get a response, it means it was a JSON request
      if (response.data?.redirect_url) {
        window.location.href = response.data.redirect_url;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create character.');
      setLoading(false);
    }
  };

  // Show creation progress
  if (loading && createdCharacter) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <div className="animate-bounce text-6xl mb-6">{'\u{1F3A8}'}</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-3">Creating your character...</h2>
        <p className="text-gray-600 mb-4">{statusMessage}</p>
        <div className="flex justify-center space-x-2">
          <div className="w-3 h-3 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-3 h-3 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-3 h-3 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Coin balance warning */}
      {!canAfford && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-800">
          You need at least 50 coins to create a character. You currently have {coinBalance} coins.
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-8">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setError(null); }}
            className={`flex-1 py-3 px-4 text-center font-medium transition-all border-b-2
              ${activeTab === tab.id
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <span className="mr-2">{tab.emoji}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab 1: From My Images */}
      {activeTab === 'ai_image' && (
        <div>
          <p className="text-gray-600 mb-4">Pick one of your AI-generated images to turn into a reusable character.</p>
          {recentAiImages.length > 0 ? (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-6">
              {recentAiImages.map((img) => (
                <div
                  key={img.id}
                  onClick={() => setSelectedImage(selectedImage?.id === img.id ? null : img)}
                  className={`rounded-lg overflow-hidden cursor-pointer transition-all transform hover:scale-105
                    ${selectedImage?.id === img.id ? 'ring-4 ring-indigo-500 shadow-lg' : 'shadow hover:shadow-md'}`}
                >
                  <img src={img.url} alt="AI image" className="w-full aspect-square object-cover" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl mb-6">
              <p className="text-gray-500">No AI images yet. Create some images first, then come back here!</p>
            </div>
          )}

          {selectedImage && (
            <div className="bg-indigo-50 rounded-xl p-6 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Character name (optional)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="We'll suggest a name if you leave this empty"
                className="w-full p-3 border border-indigo-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                maxLength={50}
              />
            </div>
          )}

          <button
            onClick={handleCreateFromImage}
            disabled={!selectedImage || !canAfford || loading}
            className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Character (50 coins)'}
          </button>
        </div>
      )}

      {/* Tab 2: Upload a Photo */}
      {activeTab === 'upload' && (
        <div>
          <p className="text-gray-600 mb-4">Upload a photo or drawing to turn into a story character.</p>

          <div className="mb-6">
            {uploadPreview ? (
              <div className="relative inline-block">
                <img src={uploadPreview} alt="Upload preview" className="w-48 h-48 object-cover rounded-xl shadow-md" />
                <button
                  onClick={() => { setUploadPreview(null); setUploadFile(null); }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-sm flex items-center justify-center"
                >
                  &times;
                </button>
              </div>
            ) : (
              <label className="block w-full p-12 border-2 border-dashed border-indigo-300 rounded-xl text-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-all">
                <span className="text-5xl block mb-3">{'\u{1F4F7}'}</span>
                <span className="text-gray-600 font-medium">Click to upload an image</span>
                <input type="file" accept="image/*" onChange={handleUploadChange} className="hidden" />
              </label>
            )}
          </div>

          <div className="bg-indigo-50 rounded-xl p-6 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Character name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="We'll suggest a name from the image"
              className="w-full p-3 border border-indigo-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              maxLength={50}
            />
          </div>

          <button
            onClick={handleCreateFromUpload}
            disabled={!uploadFile || !canAfford || loading}
            className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Character (50 coins)'}
          </button>
        </div>
      )}

      {/* Tab 3: Describe Your Character */}
      {activeTab === 'describe' && (
        <div>
          <p className="text-gray-600 mb-4">Describe what your character looks like and we will create it for you.</p>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Character name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Captain Whiskers, Luna the Dragon"
                className="w-full p-3 border border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                maxLength={50}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">What does your character look like?</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. A fluffy orange cat wearing a pirate hat and a blue vest, with a gold earring and a friendly smile. Has big green eyes and a long curly tail."
                className="w-full p-3 border border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 resize-none"
                rows={5}
                maxLength={500}
              />
              <p className="text-sm text-gray-400 mt-1">{description.length}/500</p>
            </div>
          </div>

          <div className="bg-purple-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-purple-700">
              <strong>Tips for great characters:</strong> Include species, colors, clothing, and unique features.
              The more detail you give, the more consistent your character will look across stories!
            </p>
          </div>

          <button
            onClick={handleCreateFromDescription}
            disabled={!description.trim() || description.length < 10 || !canAfford || loading}
            className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Character (50 coins)'}
          </button>
        </div>
      )}
    </div>
  );
};

CharacterCreator.propTypes = {
  recentAiImages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      url: PropTypes.string.isRequired,
    })
  ),
  coinBalance: PropTypes.number,
  csrfToken: PropTypes.string.isRequired,
};

CharacterCreator.defaultProps = {
  recentAiImages: [],
  coinBalance: 0,
};

export default CharacterCreator;
